import { ApiEndpoints } from "@shared/api/api-endpoints";
import { mxApiClient, onboardingApiClient, authApiClient } from "@shared/api/clients/apiClientFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Choice } from "./types";


// Add your feature-specific API calls here
export const mxSharedApi = {
 getNotes: async (id: string, type: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      `notes`,
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("entity_type", type);
        query.push("entity_id", id);
        return query;
      }
    );
    const response = await mxApiClient.get(url);
    return response.data ?? [];
  },

   createNote: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters("mx", "notes");
    const response = await mxApiClient.post(url, payload);
    return response.data;
  },

    getActivityLog: async (id: string, type: string) => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "mx",
        `activity-log`,
        (query) => {
          query.push("remote_utility_id", getRemoteUtilityId());
          query.push("patient_id", id);
          query.push("entity_type", type);
          return query;
        }
      );
      const response = await mxApiClient.get(url);
      return response.data ?? [];
    },
};

export const getResourceTypeList = async (remoteUtilityId: string, page?: number, limit?: number, searchData?: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/resource_json",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", "resource_type");
      query.push("show_inactive", "true");
      if (searchData) query.push("search_data", searchData);
      if (page) query.push("page", String(page));
      if (limit) query.push("limit", String(limit));
    },
  );
  const response = await onboardingApiClient.get(url);
  return response.data;
};

export const getResourceSubtypeList = async (remoteUtilityId: string, codes?: string[], page?: number, limit?: number, searchData?: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/resource_json",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("config_level", "resource_subtype");
      query.push("show_inactive", "true");
      if (codes && codes.length > 0) {
        for (const code of codes) {
          query.push("code_list", code);
        }
      }
      if (searchData) query.push("search_data", searchData);
      if (page) query.push("page", String(page));
      if (limit) query.push("limit", String(limit));
    },
  );
  const response = await onboardingApiClient.get(url);
  return response.data;
};

export const getUtilityServices = async (utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "auth",
    "utility/services",
    (query) => {
      query.push("utility_id", utilityId);
    }
  );

  const response = await authApiClient.get(url);
  return response.data.result ?? [];
};

export const getMeterReadMethods = async (remoteUtilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-read-method",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await mxApiClient.get(url);
  return response.data.result ?? response.data ?? [];
};

export const putMeterReadMethod = async (id: number, payload: { is_active: boolean }) => {
  const url = ApiEndpoints.createUrl("mx", `meter-read-method/${id}`);
  const response = await mxApiClient.put(url, payload);
  return response.data;
};

export const getMxChoices = async (
  choice: string,
  remoteUtilityId?: string,
): Promise<Choice[]> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "choices",
    [choice],
    (query) => {
      if (remoteUtilityId) {
        query.push("remote_utility_id", remoteUtilityId);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return transformJsonToObject<Choice[]>(response.data.result);
};

export const getMeterManufacturers = async (params: { remote_utility_id: string; is_pagination_required?: boolean }): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    'mx',
    'meter/manufacturer',
    (qs) => {
      qs.push('remote_utility_id', params.remote_utility_id);
      if (params.is_pagination_required !== undefined) {
        qs.push('is_pagination_required', params.is_pagination_required);
      }
    }
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getMeterModels = async (
  remoteUtilityId: string,
  manufacturer?: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    'mx',
    'meter/model',
    (qs) => {
      qs.push('remote_utility_id', remoteUtilityId);
      if (manufacturer && manufacturer.toLowerCase?.() !== 'all') {
        qs.push('manufacturer', manufacturer);
      }
    }
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getWarehouses = async (
  remoteUtilityId: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "warehouse",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_pagination_required", false);
    }
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

export const getOems = async (remoteUtilityId: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "OEM",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getMeterReasonCodesList = async (
  remoteUtilityId: string,
  page?: number,
  limit?: number,
  search?: string,
  meterReadingStatus?: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "reason-code",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      
      if (page !== undefined && limit !== undefined) {
        query.push("is_pagination_required", true);
        query.push("page", page);
        query.push("limit", limit);
      } else {
        query.push("is_pagination_required", false);
      }
      
      if (search) {
        query.push("search_data", search);
      }
      if (meterReadingStatus) {
        query.push("meter_reading_status", meterReadingStatus);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<any>(response.data);
};

export const getMeterReadingStatusList = async (
  remoteUtilityId: string,
  page?: number,
  limit?: number,
  search?: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading-status",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      
      if (page !== undefined && limit !== undefined) {
        query.push("is_pagination_required", true);
        query.push("page", page);
        query.push("limit", limit);
      } else {
        query.push("is_pagination_required", false);
      }
      
      if (search) {
        query.push("search_data", search);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<any>(response.data);
};
